import React, { useState } from "react";
import {
  Box,
  Dialog,
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserSettingsContent from "./UserSettingsContent";

/**
 * SettingsOverlay:
 *  - On small screens => MUI Drawer (temporary)
 *  - On large screens => MUI Dialog (hover overlay) 
 *     with a fixed size & scrollable content
 */
function SettingsOverlay({ open, onClose }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // Which category is selected
  const [selectedCategory, setSelectedCategory] = useState("profile");

  const handleClose = () => {
    onClose?.();
  };

  // Render the side nav + content
  function renderSettingsBody() {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",    // fill the parent (dialog or drawer)
          overflow: "hidden" // turn off parent scrolling; we'll do child scrolling
        }}
      >
        {/* Left side nav */}
        <Box
          sx={{
            width: { xs: "40%", sm: "200px" },
            borderRight: "1px solid",
            borderColor: "divider",
            p: 2,
            overflowY: "auto"  // so the nav can scroll if needed
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            Settings
          </Typography>
          <List>
            <ListItemButton
              selected={selectedCategory === "profile"}
              onClick={() => setSelectedCategory("profile")}
            >
              <ListItemText primary="Profile" />
            </ListItemButton>
            <ListItemButton
              selected={selectedCategory === "display"}
              onClick={() => setSelectedCategory("display")}
            >
              <ListItemText primary="Display" />
            </ListItemButton>
            <ListItemButton
              selected={selectedCategory === "preferences"}
              onClick={() => setSelectedCategory("preferences")}
            >
              <ListItemText primary="Preferences" />
            </ListItemButton>
            <ListItemButton
              selected={selectedCategory === "security"}
              onClick={() => setSelectedCategory("security")}
            >
              <ListItemText primary="Security" />
            </ListItemButton>
            {/* More categories if needed */}
          </List>
        </Box>

        {/* Right panel => scrollable */}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            overflowY: "auto",  // scroll within this panel for large content
            position: "relative"
          }}
        >
          {/* On mobile, we show a top-right close button inside */}
          <IconButton
            sx={{ 
              position: "absolute", 
              top: 16, 
              right: 16, 
              display: { sm: "none" } 
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>

          <UserSettingsContent category={selectedCategory} />
        </Box>
      </Box>
    );
  }

  if (!open) return null;

  // On small screens => Drawer
  if (isSmall) {
    return (
      <Drawer
        open={open}
        onClose={handleClose}
        anchor="right"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
          },
        }}
      >
        {renderSettingsBody()}
      </Drawer>
    );
  } else {
    // Large screens => a "hover" Dialog with fixed size
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false} // we want a custom width, so disable the built-in maxWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "800px",       // fixed width
            height: "600px",      // fixed height
            overflow: "hidden",   // disable scrolling on the Paper itself
            display: "flex",
            flexDirection: "column",
            position: "relative"
          },
        }}
      >
        {/* On large screens, the close button at top-right corner of the Paper */}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
        >
          <CloseIcon />
        </IconButton>

        {renderSettingsBody()}
      </Dialog>
    );
  }
}

export default SettingsOverlay;
